import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, pipe, switchMap, tap, timer } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { RegistrationLinkService } from './infra/registration-link.service';
import {
  REGISTRATION_LINK_MAX_COUNT,
  type IRegistrationLink,
  type IRegistrationLinkEmptied,
  type IRegistrationLinkPayload,
  type IRegistrationLinkSlugCheck,
  type IRegistrationLogoPayload,
  type TRegistrationExperience,
} from './entities/registration-link.model';
import type {
  IRegistrationCrmConfig,
  IRegistrationFormConfig,
  IRegistrationHealthConfig,
} from './entities/registration-page.model';

interface IRegistrationLinksState {
  links: IRegistrationLink[];
  /**
   * `true` once the list of links answered in this session; `null` until then.
   * A failure leaves it as it was.
   */
  supported: boolean | null;
  /** What the last emptying took, for the notice that follows it. */
  emptied: IRegistrationLinkEmptied | null;
  /**
   * `null` while nothing was asked, or while the answer on screen is stale.
   * It names the address and the link it was asked for, so a screen never
   * takes the answer about another link for its own.
   */
  slugCheck: IRegistrationLinkSlugCheck | null;
}

const initialState: IRegistrationLinksState = {
  links: [],
  supported: null,
  emptied: null,
  slugCheck: null,
};

/** Puts a link the Worker just answered in its place in the list, or at the end when it is new. */
const withLink = (links: IRegistrationLink[], link: IRegistrationLink): IRegistrationLink[] =>
  links.some(({ id }) => id === link.id)
    ? links.map((current) => (current.id === link.id ? link : current))
    : [...links, link];

/**
 * The gym's registration links: the list, what is done to a link as a whole
 * (create, rename, pause, empty), and the settings of each one (what its form
 * asks for, the health questionnaire, the experience it opens, the logo at
 * the top of its page, the CRM destination).
 *
 * The settings of a link named by id are saved here, not in
 * `RegistrationPageStore`: that one reaches the gym's default link only,
 * through the routes without an id. Which link is on screen is not kept here:
 * it is the route's, so nothing root-scoped leaks from one link to the next.
 *
 * Next to `RegistrationCandidateStore`, which lives for the length of the
 * counter's modal.
 */
export const RegistrationLinksStore = signalStore(
  { providedIn: 'root' },
  withState<IRegistrationLinksState>(initialState),
  withComputed((store) => ({
    /** More than one link: the counter has to be told which one to look in. */
    hasSeveral: computed(() => store.links().length > 1),
    /** The gym has every link it may have. Paused ones count, and none can be deleted. */
    atLimit: computed(() => store.links().length >= REGISTRATION_LINK_MAX_COUNT),
  })),
  withMethods(
    (store, service = inject(RegistrationLinkService), errorStore = inject(ErrorStore)) => ({
      /**
       * Every failure, a 404 included, is an error with a toast: no screen
       * takes it for "no links".
       */
      loadLinks: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadLinks') })),
          switchMap(() =>
            service.list().pipe(
              tapResponse({
                next: (links) =>
                  patchState(store, { links, supported: true, ...setPropLoaded('loadLinks') }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('loadLinks', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * Reads one link again for its counts. They go stale while
       * registrations arrive and are handled, and whoever is about to empty a
       * link has to see the ones of now.
       *
       * The answer is the whole link, and only the counts are kept from it.
       * It carries the rest of the link as it was when the Worker read it,
       * and nothing closes a pause, a rename or a save while this read is
       * out: taken whole, an answer that arrives late would undo any of them.
       * An answer for an id the list does not have changes nothing.
       *
       * Every failure is an error with a toast, the 404 included.
       */
      loadLink: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadLink') })),
          switchMap((linkId) =>
            service.get(linkId).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: store.links().map((current) =>
                      current.id === link.id ? { ...current, counts: link.counts } : current
                    ),
                    ...setPropLoaded('loadLink'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('loadLink', error) });
                },
              })
            )
          )
        )
      ),

      /** `exhaustMap`: a second click while the first is in flight would create two links. */
      createLink: rxMethod<IRegistrationLinkPayload>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('createLink') })),
          exhaustMap((payload) =>
            service.create(payload).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: withLink(store.links(), link),
                    ...setPropLoaded('createLink'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('createLink', error) });
                },
              })
            )
          )
        )
      ),

      updateLink: rxMethod<{ linkId: string; payload: IRegistrationLinkPayload }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('updateLink') })),
          switchMap(({ linkId, payload }) =>
            service.update(linkId, payload).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: withLink(store.links(), link),
                    ...setPropLoaded('updateLink'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('updateLink', error) });
                },
              })
            )
          )
        )
      ),

      setActive: rxMethod<{ linkId: string; isActive: boolean }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('setActive') })),
          switchMap(({ linkId, isActive }) =>
            service.setActive(linkId, isActive).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: withLink(store.links(), link),
                    ...setPropLoaded('setActive'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('setActive', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * Empties a link. The counts of the link change with it (nobody handled,
       * nobody waiting, more in the trash), and the Worker's answer says only
       * how many it took, so they are worked out here instead of asked again.
       *
       * `exhaustMap`: a second click would empty what the first already emptied
       * and overwrite the numbers of the notice with zeros.
       */
      emptyLink: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { emptied: null, ...setPropLoading('emptyLink') })),
          exhaustMap((linkId) =>
            service.empty(linkId).pipe(
              tapResponse({
                next: (emptied) =>
                  patchState(store, {
                    emptied,
                    links: store.links().map((link) =>
                      link.id === linkId
                        ? {
                            ...link,
                            counts: {
                              pending: 0,
                              imported: 0,
                              trashed: link.counts.trashed + emptied.trashed,
                            },
                          }
                        : link
                    ),
                    ...setPropLoaded('emptyLink'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('emptyLink', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * What the link's public form asks for. Like the four saves below, it
       * has a route and a flag of its own, so one setting being saved never
       * locks the others.
       *
       * The answer is the whole link, and only the setting just saved is kept
       * from it. Two settings of one link can be saved at the same time, and
       * each answer carries the rest of the link as it was when the Worker
       * read it: taken whole, an answer that arrives late would undo another
       * setting saved, or a pause made, in the meantime.
       */
      saveForm: rxMethod<{ linkId: string; form: IRegistrationFormConfig }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('saveForm') })),
          switchMap(({ linkId, form }) =>
            service.saveForm(linkId, form).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: store.links().map((current) =>
                      current.id === link.id
                        ? {
                            ...current,
                            form: link.form,
                            updatedAt: link.updatedAt,
                          }
                        : current
                    ),
                    ...setPropLoaded('saveForm'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('saveForm', error) });
                },
              })
            )
          )
        )
      ),

      saveCrm: rxMethod<{ linkId: string; crm: IRegistrationCrmConfig }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('saveCrm') })),
          switchMap(({ linkId, crm }) =>
            service.saveCrm(linkId, crm).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: store.links().map((current) =>
                      current.id === link.id
                        ? {
                            ...current,
                            crm: link.crm,
                            updatedAt: link.updatedAt,
                          }
                        : current
                    ),
                    ...setPropLoaded('saveCrm'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('saveCrm', error) });
                },
              })
            )
          )
        )
      ),

      saveHealth: rxMethod<{ linkId: string; health: IRegistrationHealthConfig }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('saveHealth') })),
          switchMap(({ linkId, health }) =>
            service.saveHealth(linkId, health).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: store.links().map((current) =>
                      current.id === link.id
                        ? {
                            ...current,
                            health: link.health,
                            updatedAt: link.updatedAt,
                          }
                        : current
                    ),
                    ...setPropLoaded('saveHealth'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('saveHealth', error) });
                },
              })
            )
          )
        )
      ),

      /** Whether the link opens the form in steps or the conversation. */
      saveExperience: rxMethod<{ linkId: string; experience: TRegistrationExperience }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('saveExperience') })),
          switchMap(({ linkId, experience }) =>
            service.saveExperience(linkId, experience).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: store.links().map((current) =>
                      current.id === link.id
                        ? {
                            ...current,
                            experience: link.experience,
                            updatedAt: link.updatedAt,
                          }
                        : current
                    ),
                    ...setPropLoaded('saveExperience'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('saveExperience', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * The image at the top of the link's public page. The answer brings the
       * logo and the address of the image uploaded for the link, and the two
       * are kept together: `logoUrl` means something only next to `custom`.
       */
      saveLogo: rxMethod<{ linkId: string; logo: IRegistrationLogoPayload }>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('saveLogo') })),
          switchMap(({ linkId, logo }) =>
            service.saveLogo(linkId, logo).pipe(
              tapResponse({
                next: (link) =>
                  patchState(store, {
                    links: store.links().map((current) =>
                      current.id === link.id
                        ? {
                            ...current,
                            logo: link.logo,
                            logoUrl: link.logoUrl,
                            updatedAt: link.updatedAt,
                          }
                        : current
                    ),
                    ...setPropLoaded('saveLogo'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('saveLogo', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * Whether an address can be saved on a link, or on one not created yet
       * (`linkId: null`). The address travels as it will be saved: trimmed and
       * in lower case.
       *
       * The previous answer is dropped as soon as the address changes, and so
       * is the question about it when it is still on the wire: the screen
       * never shows "available" for an address that is no longer typed, and
       * the flag stays loading until the address typed last is answered.
       *
       * For a link not created yet the Worker measures against the gym's
       * default link: that link's own address reads as free, and creating the
       * new link with it is then refused. So an address on the list is taken
       * here, whatever the Worker said. For a link that exists the Worker
       * already counts another link of the gym as taken, and its word is kept.
       *
       * A failure leaves `slugCheck` at `null` and raises no toast: the save
       * itself still refuses a taken address with a 409, so not knowing is not
       * a reason to block.
       */
      checkSlug: rxMethod<{ slug: string; linkId: string | null }>(
        pipe(
          tap(() => patchState(store, { slugCheck: null, ...setPropLoading('checkSlug') })),
          // The wait sits inside the `switchMap`, not before it as a
          // `debounceTime`: a new address has to drop the question on the wire
          // at once. Dropped only when the wait ends, its answer lands in
          // between and passes for the answer about the address on screen.
          switchMap(({ slug, linkId }) =>
            timer(350).pipe(
              switchMap(() => service.checkSlug(slug, linkId)),
              tapResponse({
                next: ({ available }) =>
                  patchState(store, {
                    slugCheck: {
                      slug,
                      linkId,
                      available:
                        available &&
                        (linkId !== null || !store.links().some((link) => link.slug === slug)),
                    },
                    ...setPropLoaded('checkSlug'),
                  }),
                error: (error: HttpErrorResponse) =>
                  patchState(store, { ...setPropError('checkSlug', error) }),
              })
            )
          )
        )
      ),

      /** Forgets the last availability answer, e.g. when the form is opened again. */
      clearSlugCheck: () => patchState(store, { slugCheck: null, ...setPropInit('checkSlug') }),

      /**
       * Clears the outcome of the four writes. The store is root-scoped, so a
       * `createLinkLoaded` left over from the previous visit would announce a
       * link nobody just created.
       */
      blankWriteState: () =>
        patchState(store, {
          emptied: null,
          ...setPropInit('createLink', 'updateLink', 'setActive', 'emptyLink'),
        }),
    })
  ),
  withReqState(
    'loadLinks',
    'loadLink',
    'createLink',
    'updateLink',
    'setActive',
    'emptyLink',
    'saveForm',
    'saveCrm',
    'saveHealth',
    'saveExperience',
    'saveLogo',
    'checkSlug'
  ),
  // Below the request states, which it reads: the methods above are put
  // together before them and do not see them.
  withMethods((store) => ({
    /**
     * Clears the outcome of the five saves of a link's settings, for the
     * screen that opens: a `saveFormLoaded` left over from the previous visit
     * would announce a save nobody just made.
     *
     * A save still in flight keeps its flag. Back at `init`, its card could be
     * edited and saved again while the first request is out, and that second
     * save would drop the answer of the first.
     */
    blankSaveState: () =>
      patchState(
        store,
        store.saveFormLoading() ? {} : { ...setPropInit('saveForm') },
        store.saveCrmLoading() ? {} : { ...setPropInit('saveCrm') },
        store.saveHealthLoading() ? {} : { ...setPropInit('saveHealth') },
        store.saveExperienceLoading() ? {} : { ...setPropInit('saveExperience') },
        store.saveLogoLoading() ? {} : { ...setPropInit('saveLogo') }
      ),
  }))
);
